import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';

interface BusinessSwitchLoaderProps {
  className?: string;
  message?: string;
}

/**
 * Full-screen loader component for business/workspace switching operations.
 * Displays a centered loading state with spinner and message following the design system.
 */
export function BusinessSwitchLoader({
  className,
  message,
}: BusinessSwitchLoaderProps) {
  const { t } = useTranslation('workspace');
  const { t: tCommon } = useTranslation('common');

  const displayMessage =
    message || t('switching-workspace', 'Switching workspace...');

  return (
    <div
      className={cn(
        'fixed inset-0 z-[99999] flex items-center justify-center',
        className
      )}
    >
      {/* Backdrop with blur effect */}
      <div className="absolute inset-0 bg-black/10 backdrop-blur-sm" />

      {/* Loader card */}
      <div className="relative z-10 flex flex-col items-center gap-4 rounded-lg border border-grayscale-200 bg-white p-8 shadow-xl">
        {/* Spinner */}
        <Spinner size="lg" className="text-primary" />

        {/* Message */}
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-base font-medium text-grayscale-900">
            {displayMessage}
          </p>
          <p className="text-sm text-grayscale-500">
            {tCommon('please-wait', 'Please wait...')}
          </p>
        </div>
      </div>
    </div>
  );
}
